/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef } from 'react';

/** Escape levels, in the order their layers are drawn on the `--z-*` scale. */
export const ESCAPE_LEVEL = {
  /** `--z-overlay`: a dialog opened from the page. One opened from a dialog adds its depth. */
  dialog: 0,
  /** `--z-overlay-blocking`, above every dialog depth. */
  blocking: 10,
  /** `--z-lightbox`: an image or video viewer, above the dialog it was opened from. */
  lightbox: 11,
} as const;

interface EscapeLayer {
  level: number;
}

/** The layers now open, in the order they opened. */
const openLayers: EscapeLayer[] = [];

/** The layer drawn over the others: the highest level and, on that level, the last to open. */
export function topOf<T extends EscapeLayer>(layers: readonly T[]): T | undefined {
  return layers.reduce<T | undefined>(
    (top, layer) => (top && top.level > layer.level ? top : layer),
    undefined,
  );
}

const topLayer = () => topOf(openLayers);

/**
 * Closes a dialog or viewer on Escape while it is open and drawn over every other open one, so a
 * single Escape never takes down what it was opened from as well. One that is not `dismissable`
 * stays on top all the same, keeping Escape from the layers beneath it.
 */
export function useEscapeLayer(
  onEscape: () => void,
  {
    level,
    open = true,
    dismissable = true,
  }: { level: number; open?: boolean; dismissable?: boolean },
): void {
  const self = useRef<EscapeLayer>({ level });

  // Joins as it opens and leaves as it closes, not on each run of the effect below: an inline
  // onEscape re-runs that on every render, and re-joining there would lift a covered layer back up.
  useEffect(() => {
    if (!open) return;
    const layer = self.current;
    openLayers.push(layer);
    return () => {
      openLayers.splice(openLayers.indexOf(layer), 1);
    };
  }, [open]);

  useEffect(() => {
    self.current.level = level;
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !dismissable || topLayer() !== self.current) return;
      e.preventDefault();
      onEscape();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onEscape, level, open, dismissable]);
}
